import React, { useState } from 'react';

import { RightOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const Section = styled.section`
  background: #fff;
  border: 1px solid #e9ebee;
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;
`;

/**
 * A linha do cabecalho: o botao que abre e fecha, mais uma acao opcional ao lado.
 *
 * A acao fica FORA do botao de propósito — button dentro de button é HTML inválido, e o
 * clique na ação abriria a seção junto.
 */
const HeadRow = styled.div`
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid transparent;
`;

const Action = styled.div`
  display: flex;
  align-items: center;
  padding: 0 14px 0 0;
`;

const Head = styled.button<{ open: boolean }>`
  flex: 1;
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 10px 14px;
  border: none;
  border-bottom: ${({ open }) => (open ? '1px solid #f2f4f6' : 'none')};
  background: #fff;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #fafbfc;
  }
`;

const Title = styled.span`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #8a9099;
`;

/**
 * O resumo aparece fechado também — é ele que faz a seção fechada ainda dizer algo.
 *
 * Sem resumo, uma seção retraída é uma caixa com título: obriga a abrir para saber se vale
 * abrir.
 */
const Summary = styled.span`
  flex: 1;
  font-size: 11.5px;
  color: #6b7280;
  text-align: right;
`;

const Chevron = styled.span<{ open: boolean }>`
  color: #757b88;
  font-size: 11px;
  transform: rotate(${({ open }) => (open ? '90deg' : '0deg')});
  transition: transform 0.15s ease;
  align-self: center;
`;

export type CollapsibleSectionProps = {
  title: React.ReactNode;

  /** Linha curta que continua visível com a seção fechada. */
  summary?: React.ReactNode;

  /** Fechada por padrão: a tela abre mostrando a lista, que é o assunto principal. */
  defaultOpen?: boolean;

  /**
   * Botão à direita do cabeçalho, fora do que abre e fecha.
   *
   * Fica aqui, e não no corpo, para continuar alcançável com a seção fechada: uma ação que
   * só existe aberta obriga a expandir para desfazer o que a seção fez.
   */
  action?: React.ReactNode;

  children: React.ReactNode;
};

/**
 * Seção que abre e fecha (MV2-1706).
 *
 * A aba Estações ganhou duas áreas de apoio — o token da conta e o cadastro das estações —
 * e as duas empurravam a lista para baixo da dobra. Fechadas por padrão, cada uma vira uma
 * linha: quem precisa abre, e quem veio ver a lista vê a lista.
 *
 * O cabeçalho é um `button` de verdade, com aria-expanded: teclado e leitor de tela
 * ganham o comportamento de graça, o que uma `div` com onClick não daria.
 */
export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  summary,
  defaultOpen = false,
  action,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Section>
      <HeadRow>
        <Head type="button" open={open} aria-expanded={open} onClick={() => setOpen(!open)}>
          <Chevron open={open}>
            <RightOutlined />
          </Chevron>
          <Title>{title}</Title>
          {summary && <Summary>{summary}</Summary>}
        </Head>

        {action && <Action>{action}</Action>}
      </HeadRow>

      {/* O corpo só é montado aberto: fechado ele não custa render nem confunde a busca da página. */}
      {open && children}
    </Section>
  );
};

export default CollapsibleSection;
